/**
 * Shared "does this employee row belong to this manager?" matcher.
 *
 * Used by BOTH the Task module (`src/views/apps/task/mixins/
 * taskTeamMixin.js`) and the Attendance module (`src/views/dashboard/
 * attendance/mixins/teamDataMixin.js`) wherever a manager-scoped employee
 * list needs to be derived/verified from a raw employee row's manager-id
 * field(s) against a target manager's `emp_id`.
 *
 * Extracted here after the SAME bug was found independently in both
 * places: a fragile "first non-null field, strict `===`" check —
 * `employee.mgr_id ?? employee.manager_id ?? employee.manager ?? employee.account_manager`
 * compared with `===` — silently dropped real direct reports whenever
 * either (a) a HIGHER-priority field happened to be populated with
 * something OTHER than the target manager (so a correct match on a
 * LOWER-priority field was never even checked), or (b) the id was typed
 * into the manager-id field with different case/whitespace/hyphenation
 * than the target `emp_id` (e.g. `"SH-002"` vs `"sh002"`). Confirmed in
 * production on the Task module's version of this check: a manager with 8
 * real direct reports only saw 4. Fixing both call sites with their own
 * separate copies of the corrected logic would just reintroduce the same
 * drift risk the next time a new manager-id field convention shows up
 * (see `tl_emp_id` below, already a THIRD field-naming convention in this
 * codebase for the same real-world relationship — c.f.
 * `MyCalls.vue#managerOptions`) — hence one shared implementation instead.
 */

/** Every field name this codebase has been seen storing "this employee's manager's emp_id" under. */
export const MANAGER_ID_FIELDS = ['mgr_id', 'manager_id', 'manager', 'account_manager', 'tl_emp_id']

/** Trim + uppercase + strip non-alphanumerics, so `"SH-002"` / `"sh002"` / `" SH 002 "` all normalize identically. */
export function normalizeId(value) {
  if (value === undefined || value === null || value === '') return null
  return String(value).trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || null
}

/**
 * Additionally strips LEADING ZEROS from a trailing run of digits on an
 * already-`normalizeId()`-d value, so `"SH002"` and `"SH2"` — the same id,
 * just padded differently in two different places the codebase happens to
 * store/type it — compare equal too. Only touches strings that are
 * entirely `<letters><digits>` (the `^...$` anchors mean anything else,
 * e.g. a trailing non-digit suffix, is returned unchanged) — deliberately
 * NOT folded into `normalizeId()` itself, since stripping zeros is a lossy
 * transform only appropriate for a "same real id?" equality check like
 * `filterToManagerReports()` below, not for general-purpose id
 * normalization elsewhere in the codebase.
 */
function stripLeadingZeros(normalized) {
  if (!normalized) return normalized
  return normalized.replace(/^([A-Z]*)0*(\d+)$/, '$1$2')
}

/** All non-empty manager-id-field values present on `member`, normalized (may be more than one — e.g. legacy + current field both set). */
export function managerIdCandidates(member) {
  return MANAGER_ID_FIELDS
    .map(field => normalizeId(member?.[field]))
    .filter(value => value !== null)
}

/**
 * Narrows `members` down to rows actually managed by `managerEmpId`.
 *
 * Per-row matching, in order:
 *
 * 1. **No manager-id field at all on this row** (`managerIdCandidates()`
 *    returns empty) -> included only when `trustRowsWithNoManagerField` is
 *    true, excluded otherwise (the safe default — see that param's own
 *    doc for why this can't be a blanket "always include" for every
 *    caller).
 * 2. **Has at least one manager-id field** -> included only if ANY
 *    candidate matches `managerEmpId`, checked BOTH as an exact
 *    normalized match AND a leading-zero-insensitive match (`"SH002"` ==
 *    `"SH2"`, see `stripLeadingZeros()`) — otherwise excluded, since this
 *    row has positive evidence of a DIFFERENT manager.
 *
 * Skips step 2 entirely (falls back to returning `members` unfiltered) when
 * NONE of the rows carry a recognizable manager-id field, so a backend
 * response shape that simply doesn't expose it doesn't wipe out a
 * legitimate manager's team wholesale.
 *
 * Logs a dev-only diagnostic (stripped in production builds) for any
 * EXCLUDED row (i.e. one that failed step 2) whose manager-id field(s)
 * look like a near-miss of `managerEmpId` (same digits, different
 * formatting/prefix) — that pattern means the exclusion is probably a
 * data-shape issue worth a second look, not a genuinely different manager.
 *
 * @param {object[]} members Raw employee rows (e.g. from `userService.getAllTeamMembersList()` / `getAllUsers()`).
 * @param {string|number} managerEmpId The manager's own `emp_id`.
 * @param {string} [logPrefix] Tags the dev-only near-miss warning (e.g. `'[task]'` / `'[attendance]'`) so it's obvious which caller logged it.
 * @param {boolean} [trustRowsWithNoManagerField] Whether a row with ZERO
 *   populated manager-id fields should be included by default rather than
 *   excluded. Safe/correct ONLY when `members` came from an endpoint that
 *   is ALREADY contractually scoped to `managerEmpId` (e.g.
 *   `GET users/team-members?emp_id=<manager>`, used by the Task module) —
 *   there, this filter exists purely to catch the KNOWN backend quirk
 *   where that scoping breaks down for broad-ACL callers (see the header
 *   comment), not to second-guess every row; a row with no manager-id
 *   field is exactly as likely to be a genuine direct report
 *   (inconsistently-populated legacy data) as anything else, and there's
 *   no data on it to decide either way, so it's trusted to the endpoint's
 *   own scoping rather than dropped. (This was in fact the confirmed root
 *   cause of a real "still missing employees after the first fix" report
 *   in the Task module: some rows in an otherwise-correctly-scoped
 *   response had zero manager-id fields populated and were being silently
 *   excluded by the unconditional-exclude version of this check.)
 *   Defaults to `false` (exclude) because it is UNSAFE for a caller whose
 *   `members` is NOT manager-scoped to begin with — e.g. the Attendance
 *   module's `teamDataMixin.js` call sites, which filter
 *   `userService.getAllUsers()` (literally every employee in the company)
 *   down to one account manager's team; defaulting those to "include
 *   when no data" would leak every field-less employee into EVERY account
 *   manager's filtered list.
 */
export function filterToManagerReports(members, managerEmpId, logPrefix = '[manager-filter]', trustRowsWithNoManagerField = false) {
  if (!Array.isArray(members) || !members.length) return members || []
  const hasManagerField = members.some(member => managerIdCandidates(member).length > 0)
  if (!hasManagerField) return members

  const targetId = normalizeId(managerEmpId)
  const targetLoose = stripLeadingZeros(targetId)
  const targetDigits = String(managerEmpId).replace(/\D/g, '')

  const included = []
  members.forEach(member => {
    const candidates = managerIdCandidates(member)

    // No manager-id field on this specific row — see `trustRowsWithNoManagerField`'s doc for why this can't unconditionally default to "include".
    if (!candidates.length) {
      if (trustRowsWithNoManagerField) included.push(member)
      return
    }

    // Exact normalized match, or same id with different zero-padding.
    const isMatch = candidates.includes(targetId)
      || candidates.some(candidate => stripLeadingZeros(candidate) === targetLoose)
    if (isMatch) {
      included.push(member)
      return
    }

    if (process.env.NODE_ENV !== 'production' && targetDigits) {
      const nearMiss = candidates.some(candidate => {
        const digits = candidate.replace(/\D/g, '')
        return digits && digits === targetDigits && candidate !== targetId
      })
      if (nearMiss) {
        console.warn(
          `${logPrefix} Direct-report filter excluded a member whose manager-id field looks like a near-miss of the current manager's emp_id (same digits, different formatting) — likely a data-shape issue, not a genuinely different manager:`,
          { managerEmpId, targetNormalized: targetId, member, candidates },
        )
      }
    }
  })
  return included
}

export default {
  MANAGER_ID_FIELDS,
  normalizeId,
  managerIdCandidates,
  filterToManagerReports,
}
